import CacheCollection from 'collection/CacheCollection'
import {fetchDqAPI} from 'module/Dq/api/DqApi'
import {alertServerError} from 'module/ConfirmDialog/component/ConfirmDialogContainer'
import {CustomFieldTO} from 'generated/JavaTO'
import {CFModel} from 'module/CustomField/type/CFType'
import {getDqParamsObject, parseDqParamFromSerializedString} from 'module/CustomField/util/DqParam'
import {JSX} from 'react'
import {CFAttrKey} from 'module/CustomField/constant/CFAttrConstants'
import {CF} from 'module/CustomField/util/CustomField'
import {isDqSearchable, parseDqResponseTO_deprecated} from 'module/Dq/util/DqParser'

const cache = new CacheCollection<Promise<any> | any>();
const dqFirstRequestMap: { [dqkey in string]?: Promise<void> } = {} // wait for first request to finalize to send further requests

type DqElementClientSideExt = {
	_specialFirstEntry?: boolean // deprecated ReactSelect parameter
	isDisabled?: boolean // deprecated ReactSelect parameter
	memo?: boolean
}
export type DqElement<ValueType extends string | number = string | number> = {
	value?: ValueType | null
	label?: string | number | null
	jsx?: JSX.Element
	// all attributes come as string from server! other types can only be used on client
	azetext?: string // @fk pls doc here
	color?: string // font color
	background?: string // background color
	html?: string 	// overrides displayFormat and is exactly how it is displayed
	dotcolor?: string // dot at the end
	valuebackground?: string, // displayValue style (when it should differ from "background")
	valuecolor?: string, // displayValue style (when it should differ from "color")
	textcss?: string // css applied on wrapper
	tooltip?: string
	copy?: string // copied text to clipboard (i.e., for DqLabel input, when a special key/business key should get copied)
	hidden?: boolean // return anything or null, 'N', 0, 'false' in dq
	disabled?: boolean // return anything or null, 'N', 0, 'false' in dq
	sortnr?: string | number
	pos?: string | number // for remoteSetterDq when specific positions should get set
} & DqElementClientSideExt

function getRequestCacheKey({dqkey, params, url, filter, isAze}: SearchDqArgs) {
	return [dqkey, params && JSON.stringify(params, Object.keys(params).sort()), url || '', filter, isAze || false].join('|')
}

interface SearchDqArgs {
	dqkey: string
	params?: { [key in string]: string | number | null | undefined }
	filter?: string | number
	url?: string
	dataId?: string
	dataText?: string
	isAze?: boolean
}

function _searchDq(args: SearchDqArgs): Promise<DqElement[]> {
	const {
		dqkey,
		dataId = 'value',
		dataText = 'label',
	} = args
	const searchable = isDqSearchable(dqkey)
	args = (args.filter || args.filter === 0) && searchable ? {...args, filter: (args.filter + '').trim()} : {...args, filter: ''}
	let requestId = getRequestCacheKey(args)
	let cached = cache.getCache(requestId)
	if (cached) {
		return cached
	} else {
		const req = fetchDqAPI({...args}).then(function (response) {
			const data = response.data || {}
			if (!data.searchable && args.filter) {
				cache.removeCache(requestId)
				requestId = getRequestCacheKey({...args, filter: ''})
			}
			cache.setCache(requestId, req, data.cacheTTL)
			return parseDqResponseTO_deprecated(data, dataId, dataText)
		})
		cache.setCache(requestId, req)
		req.catch((err) => {
			cache.removeCache(requestId, req)
			void alertServerError(err)
		})
		return req;
	}
}
export function searchDq(args: SearchDqArgs): Promise<DqElement[]> {
	if (!args.url) args = {...args, url: '../json/aacf'}
	const firstRequest = dqFirstRequestMap[args.dqkey]
	if (firstRequest) {
		// wait for first request, then do
		return firstRequest.then(() => _searchDq(args))
	} else {
		const req = _searchDq(args)
		dqFirstRequestMap[args.dqkey] = req.then(() => void 0).catch(() => void 0)
		return req
	}
}

export function searchDqWithCFDef(cfDef: CustomFieldTO, model: CFModel, pos?: number, filter?: string, options?: { textView?: boolean, isAze?: boolean }) {
	const {textView, isAze} = options || {}
	let dqKey = cfDef.dqKey
	let dqParams = cfDef.dqParams
	if (textView) {
		const altDqKey = CF.getCFAttr(cfDef, CFAttrKey.dqLabelInTextViewDqKey)
		if (altDqKey) {
			dqKey = altDqKey.value
			dqParams = cfDef.cfAttrs?.filter(x => x.key === CFAttrKey.dqLabelInTextViewDqParam)
				.sort((a, b) => (a.type || '0').localeCompare(b.type || '0'))
				.map(x => parseDqParamFromSerializedString(x?.value))
		}
	}
	return searchDq({
		filter,
		dqkey: dqKey,
		params: getDqParamsObject(dqParams, model, pos),
		isAze,
	})
}